// Reine Rechnung der Seite „Läufe" (System → Runs) nach docs/gestaltung.md
// (03.10.2026). Ohne Svelte, damit testbar (lib/laeufe.test.mjs).
//
// Normales ist leise: ein gelungener Lauf ist ein graues „ok", kein
// grünes Abzeichen. Rot ist ein Fehler, der noch gilt -- also nur, wenn
// der letzte Lauf derselben Aufgabe fehlschlug. Ein Fehler, nach dem die
// Aufgabe wieder gelaufen ist, ist Geschichte und steht leise da.

import type { LageTeil } from "../lage";
import type { RunRecord } from "../types";

export type LaufZustand = "ok" | "failed" | "running" | "cancelled";

export function zustandVon(status: string): LaufZustand {
  if (status === "running") return "running";
  if (status === "ok" || status === "success") return "ok";
  if (status === "cancelled") return "cancelled";
  return "failed";
}

// "ok" = Normales (StatusBadge: leise, mit „alles, auch ok" grün); "" = kein
// Urteil (abgebrochen, überholter Fehler) -- bleibt immer leise.
export function laufWort(status: string, nochAktuell = true): { text: string; ton: "" | "ok" | "danger" | "accent" } {
  switch (zustandVon(status)) {
    case "running":
      return { text: "läuft", ton: "accent" };
    case "ok":
      return { text: "ok", ton: "ok" };
    case "cancelled":
      return { text: "abgebrochen", ton: "" };
    default:
      return nochAktuell ? { text: "Fehler", ton: "danger" } : { text: "Fehler, seither ok", ton: "" };
  }
}

/** Je Aufgabe der neueste Lauf (Liste beliebig sortiert). */
export function letzteJeAufgabe(runs: Pick<RunRecord, "id" | "task_id" | "started_at" | "status">[]): Map<string, string> {
  const neueste = new Map<string, { id: string; t: string }>();
  for (const r of runs) {
    const n = neueste.get(r.task_id);
    if (!n || r.started_at > n.t) neueste.set(r.task_id, { id: r.id, t: r.started_at });
  }
  return new Map([...neueste].map(([k, v]) => [k, v.id]));
}

/** Gilt dieser Fehler noch? Ja, wenn er der neueste Lauf seiner Aufgabe ist. */
export function fehlerGilt(r: Pick<RunRecord, "id" | "task_id">, letzte: Map<string, string>): boolean {
  return letzte.get(r.task_id) === r.id;
}

/** Lage der Läufe im Zeitraum: was läuft, welche Fehler noch gelten,
 *  die erledigten leise, sonst „keine Fehler". */
export function laeufeLage(runs: Pick<RunRecord, "id" | "task_id" | "task_name" | "started_at" | "status">[]): LageTeil[] {
  const letzte = letzteJeAufgabe(runs);
  const teile: LageTeil[] = [];
  const laufen = runs.filter((r) => zustandVon(r.status) === "running").length;
  const fehler = runs.filter((r) => zustandVon(r.status) === "failed");
  const geltend = fehler.filter((r) => fehlerGilt(r, letzte));
  if (geltend.length) {
    const namen = [...new Set(geltend.map((r) => r.task_name))];
    const liste = namen.length <= 2 ? namen.join(" und ") : `${namen.slice(0, 2).join(", ")} und ${namen.length - 2} weitere`;
    teile.push({ text: `fehlgeschlagen: ${liste}`, ton: "danger" });
  }
  if (laufen) teile.push({ text: `${laufen} ${laufen === 1 ? "läuft" : "laufen"}`, ton: "accent" });
  const erledigt = fehler.length - geltend.length;
  if (!geltend.length) teile.push({ text: "keine offenen Fehler", ton: "ok" });
  if (erledigt) teile.push({ text: `${erledigt} Fehler seither ok` });
  return teile;
}

/** Live-Ausgabe eines laufenden Laufs aus dem Stand des Servers
 *  (`/v2-api/tasks/runs/<id>/live`, alle Zeilen bis zum Abruf) und dem,
 *  was seit dem Abruf über den WS-Kanal kam. Der Stand ERSETZT das
 *  Bisherige -- er ist vollständig, auch über eine Verbindungslücke
 *  hinweg. Vom Mitgekommenen bleibt nur, was nach dem Stand kam: was davon
 *  schon an seinem Ende steht, fällt weg (nur ganze Zeilen).
 *  Repo-Prüfung 21.10.2026, zweite Runde: bisher wurde der Stand
 *  VORANGESTELLT -- Zeilen doppelt, und nach einem Abriss fehlten die aus
 *  der Lücke. */
export function liveZusammen(stand: string, seither: string): string {
  if (!seither) return stand;
  // Längstes Anfangsstück aus ganzen Zeilen von `seither`, mit dem
  // `stand` endet (und das dort an einer Zeilengrenze beginnt).
  let schnitt = 0;
  for (let i = seither.indexOf("\n"); i >= 0; i = seither.indexOf("\n", i + 1)) {
    const kopf = seither.slice(0, i + 1);
    const ab = stand.length - kopf.length;
    if (ab >= 0 && stand.endsWith(kopf) && (ab === 0 || stand[ab - 1] === "\n")) schnitt = i + 1;
  }
  return stand + seither.slice(schnitt);
}
